import React from 'react';
import { Block } from 'baseui/block';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';

export interface ChatTranslationStampProps {
  /** Informational text shown above (e.g., "This is a translated message") */
  infoText?: string;
  /** Click handler for the action text (e.g., Hide/Show Original) */
  onClick?: () => void;
  /** Action link text (e.g., "Hide Original") */
  actionText?: string;
  errorText?: string;
  dataTestId?: string;
}

/**
 * ChatTranslationStamp
 * ---------------------------------------------------------------------
 * Renders a small right-aligned informational footer for translated
 * chat messages — typically includes an info line and an actionable link.
 */
export const ChatTranslationStamp = ({
  infoText = '',
  actionText = '',
  errorText = '',
  onClick,
  dataTestId,
}: ChatTranslationStampProps) => {
  return (
    <Block
      data-testid={dataTestId}
      overrides={{
        Block: {
          style: ({ $theme }: { $theme: CustomTheme }) => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            lineHeight: 1.4,
          }),
        },
      }}
    >
      {/* Info text */}
      {infoText && (
        <Block
           data-testid={`${dataTestId}-info`}
          overrides={{
            Block: {
              style: ({ $theme }: { $theme: CustomTheme }) => ({
                fontSize: $theme.typography.LabelSmall?.fontSize ,
                color: $theme.colors.contentSecondary,
                marginBottom: $theme.sizing.scale100,
              }),
            },
          }}
        >
          {infoText}
        </Block>
      )}

      {/* Error text */}
      {errorText && (
        <Block
          data-testid={`${dataTestId}-error`}
          overrides={{
            Block: {
              style: ({ $theme }: { $theme: CustomTheme }) => ({
                fontSize: $theme.typography.LabelSmall?.fontSize,
                color: '#BE0000',
                marginBottom: $theme.sizing.scale100,
              }),
            },
          }}
        >
          {errorText}
        </Block>
      )}

      {/* Action link */}
      {actionText && (
        <Block
          role="button"
          tabIndex={0}
          data-testid={`${dataTestId}-action`}
          onClick={onClick}
          overrides={{
            Block: {
              style: ({ $theme }: { $theme: CustomTheme }) => ({
                cursor: 'pointer',
                color: $theme.colors.chatStampButtonText,
                fontSize: $theme.typography.LabelSmall?.fontSize,
                fontWeight: 600,
                ':hover': {
                  textDecoration: 'underline',
                },
              }),
            },
          }}
        >
          {actionText}
        </Block>
      )}
    </Block>
  );
};
